/**
 * Phase 126.B — Classroom Support Hub index.
 *
 * Lists the three reference pages (ADHD, Giftedness, 2e) and frames
 * the free top-of-funnel positioning. Public, no-login, shareable.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Brain, Check, Layers, Puzzle, Sparkles } from "lucide-react";
import useDocumentMeta from "../../lib/useDocumentMeta";
import { HUB_GUIDE_SLUGS, getReadGuides } from "../../lib/hubReadGuides";
import AuthAwareSupportLayout from "../../components/classroom-support/AuthAwareSupportLayout";

const PROFILES = [
  {
    slug: "adhd",
    title: "ADHD in the Classroom",
    blurb:
      "Movement, novelty, chunks, immediate feedback. The strengths these students bring and what helps when the friction shows up.",
    icon: Brain,
    accent: "from-[#96ded1]/30 to-[#96ded1]/10",
    links: [
      { to: "/classroom-support/adhd", label: "Read the guide", testid: "hub-index-card-adhd-read" },
      { to: "/classroom-support/adhd/generator", label: "Generate a plan", testid: "hub-index-card-adhd-generate" },
    ],
  },
  {
    slug: "giftedness",
    title: "Giftedness in the Classroom",
    blurb:
      "Depth over volume. Open-ended challenge, real audiences, productive-failure framing for perfectionism — what to do once the worksheet is done in five minutes.",
    icon: Sparkles,
    accent: "from-[#f6c177]/30 to-[#f6c177]/10",
    links: [
      { to: "/classroom-support/giftedness", label: "Read the guide", testid: "hub-index-card-giftedness-read" },
      { to: "/classroom-support/giftedness/generator", label: "Generate a plan", testid: "hub-index-card-giftedness-generate" },
    ],
  },
  {
    slug: "autism",
    title: "Autism in the Classroom",
    blurb:
      "Predictability, explicit expectations, sensory-aware environment, and respect for how the student communicates. Identity-affirming, strengths-first.",
    icon: Puzzle,
    accent: "from-[#7aa2f7]/30 to-[#7aa2f7]/10",
    links: [
      { to: "/classroom-support/autism", label: "Read the guide", testid: "hub-index-card-autism-read" },
      { to: "/classroom-support/autism/generator", label: "Generate a plan", testid: "hub-index-card-autism-generate" },
    ],
  },
  {
    slug: "2e",
    title: "Twice-Exceptional (2e)",
    blurb:
      "Gifted AND ADHD. The two cancel out, the student gets called 'inconsistent', and years go by. Here's how to spot it and what 'both at once' looks like in your room.",
    icon: Layers,
    accent: "from-[#c4a7e7]/30 to-[#c4a7e7]/10",
    links: [
      { to: "/classroom-support/2e", label: "Read the guide", testid: "hub-index-card-2e-read" },
      { to: "/classroom-support/adhd/generator", label: "Generate a plan for ADHD", testid: "hub-index-card-2e-generate-adhd" },
      { to: "/classroom-support/giftedness/generator", label: "Generate a plan for Giftedness", testid: "hub-index-card-2e-generate-gifted" },
    ],
  },
];

export default function ClassroomSupportHub() {
  useDocumentMeta({
    title: "Classroom Support Hub — ADHD, Giftedness, Autism, 2e",
    description:
      "Free, no-login teacher-to-teacher guides for supporting students with ADHD, gifted, autistic, and twice-exceptional learners. Strengths-first classroom strategy — no diagnosis.",
    image: "https://atomicedu.ca/og/classroom-support.png",
    url: "https://atomicedu.ca/classroom-support",
    type: "website",
  });

  const [readGuides, setReadGuides] = useState([]);
  useEffect(() => {
    setReadGuides(getReadGuides());
  }, []);

  const readCount = readGuides.length;
  const totalCount = HUB_GUIDE_SLUGS.length;
  const showPromo = readCount >= 1 && readCount < totalCount;
  const remainingCount = totalCount - readCount;

  return (
    <AuthAwareSupportLayout title="Classroom Support">
      {({ inShell }) => (
        <div
          className="min-h-screen bg-[#f9fafb] text-[#1c2d3d]"
          data-testid="classroom-support-hub"
        >
          {/* Public top bar — only shown when NOT signed in. Signed-in
              users see the AppShell rail + InPageTopBar instead, with
              "Classroom Support" already in the page title. */}
          {!inShell ? (
            <header className="border-b border-[#4a6984]/10 bg-white">
              <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
                <Link
                  to="/"
                  className="text-sm font-bold text-[#4a6984] hover:text-[#1c2d3d]"
                  data-testid="hub-index-home"
                >
                  ← Atomic Edu
                </Link>
                <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70">
                  Free · No login
                </span>
              </div>
            </header>
          ) : null}

      <section className="max-w-4xl mx-auto px-5 pt-14 pb-10">
        <p className="text-xs uppercase tracking-[0.28em] font-extrabold text-[#4a6984]/70 mb-3">
          Classroom Support Hub
        </p>
        <h1
          className="font-black text-4xl sm:text-5xl lg:text-6xl text-[#1c2d3d] tracking-tight leading-tight"
          data-testid="hub-index-title"
        >
          Four learners every classroom has — and rarely serves well.
        </h1>
        <p className="mt-6 text-lg text-[#4a6984] leading-relaxed max-w-2xl">
          Teacher-to-teacher guides for ADHD, giftedness, autism, and 2e.
          Strengths first. Classroom strategy, not diagnosis. Read one or
          all four — they&apos;re written as a set.
        </p>
      </section>

      {showPromo ? (
        <section
          className="max-w-5xl mx-auto px-5 -mt-4 mb-8"
          data-testid="hub-read-all-promo"
        >
          <div className="ae-card p-5 sm:p-6 flex items-start sm:items-center gap-4 sm:gap-5 flex-col sm:flex-row border border-[#96ded1]/40 bg-gradient-to-r from-[#96ded1]/10 to-transparent">
            <span className="h-11 w-11 rounded-lg bg-[#96ded1]/25 grid place-items-center shrink-0">
              <BookOpen className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
            </span>
            <div className="flex-1">
              <p
                className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-1.5"
                data-testid="hub-read-all-promo-eyebrow"
              >
                You&apos;ve read {readCount} of {totalCount}
              </p>
              <p
                className="font-extrabold text-lg text-[#1c2d3d] leading-snug"
                data-testid="hub-read-all-promo-headline"
              >
                {remainingCount === 1
                  ? "One more guide to go — they're written as a set."
                  : `${remainingCount} more guides to go — they're written as a set.`}
              </p>
              <p className="text-sm text-[#4a6984] mt-1.5 leading-relaxed">
                The four profiles overlap in real classrooms — reading all of
                them gives the lens you need to spot what&apos;s really going
                on. A free account also unlocks the plan generators.
              </p>
            </div>
            <Link
              to="/auth"
              data-testid="hub-read-all-promo-cta"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#96ded1] hover:bg-[#7fd0c1] text-[#1c2d3d] text-sm font-extrabold transition shrink-0 group/btn"
            >
              <span>Create free account</span>
              <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-0.5 transition" />
            </Link>
          </div>
        </section>
      ) : null}

      <main className="max-w-5xl mx-auto px-5 pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROFILES.map(({ slug, title, blurb, icon: Icon, accent, links }) => {
            const isRead = readGuides.includes(slug);
            return (
            <div
              key={slug}
              data-testid={`hub-index-card-${slug}`}
              className="ae-card p-6 flex flex-col relative"
            >
              {isRead ? (
                <span
                  className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#96ded1]/25 border border-[#96ded1]/60 text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#1c2d3d]"
                  data-testid={`hub-index-card-${slug}-read-badge`}
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                  Read
                </span>
              ) : null}
              <span
                className={`inline-flex h-11 w-11 rounded-lg bg-gradient-to-br ${accent} grid place-items-center mb-4`}
              >
                <Icon className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
              </span>
              <h2 className="font-extrabold text-xl text-[#1c2d3d] tracking-tight">
                {title}
              </h2>
              <p className="text-sm text-[#4a6984] mt-2.5 leading-relaxed flex-1">
                {blurb}
              </p>
              <div className="mt-5 flex flex-col gap-2">
                {links.map((link, i) => (
                  <Link
                    key={link.testid}
                    to={link.to}
                    data-testid={link.testid}
                    className={
                      i === 0
                        ? "inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-[#96ded1] hover:bg-[#7fd0c1] text-[#1c2d3d] text-sm font-extrabold transition group/btn"
                        : "inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-full bg-white text-[#1c2d3d] text-sm font-bold border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition group/btn"
                    }
                  >
                    <span>{link.label}</span>
                    <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-0.5 transition" />
                  </Link>
                ))}
              </div>
            </div>
            );
          })}
        </div>

        <section
          className="mt-14 ae-card p-7"
          data-testid="hub-index-posters-cta"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
                Free for your classroom wall
              </p>
              <p className="font-extrabold text-[#1c2d3d] text-xl tracking-tight">
                Transferable Skills posters
              </p>
              <p className="mt-1 text-sm text-[#4a6984] leading-relaxed">
                Seven print-ready PDFs — one per Ontario transferable
                skill — with student-friendly &ldquo;I can&rdquo; look-fors.
                No login.
              </p>
            </div>
            <Link
              to="/posters"
              data-testid="hub-index-posters-link"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition shrink-0"
            >
              Open the posters &rarr;
            </Link>
          </div>
        </section>

        <section
          className="mt-6 ae-card p-6 sm:p-7 border-l-4 border-l-[#3EB489]"
          data-testid="hub-index-support-tools-card"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-2">
                Free printable classroom tools
              </p>
              <p className="font-extrabold text-[#1c2d3d] text-xl tracking-tight">
                Special-ed Classroom Tools Pack
              </p>
              <p className="mt-1 text-sm text-[#4a6984] leading-relaxed">
                Five blank, teacher-fillable PDFs — task checklist, visual
                schedule, first-then board, reward chart, and a daily
                check-in / regulation scale. No login.
              </p>
            </div>
            <Link
              to="/support-tools"
              data-testid="hub-index-support-tools-link"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition shrink-0"
            >
              Open the tools pack &rarr;
            </Link>
          </div>
        </section>

        <section
          className="mt-14 ae-card p-7"
          data-testid="hub-index-non-negotiables"
        >
          <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-3">
            How we wrote these
          </p>
          <ul className="space-y-2.5 text-[#4a6984] leading-relaxed">
            <li className="flex items-start">
              <span className="ae-marker mt-2.5" />
              <span>
                <strong className="text-[#1c2d3d]">Classroom strategy, not diagnosis.</strong>{" "}
                Nothing here labels a student. If you recognize one, the
                next step is your school team — not a label from this page.
              </span>
            </li>
            <li className="flex items-start">
              <span className="ae-marker mt-2.5" />
              <span>
                <strong className="text-[#1c2d3d]">Strengths first.</strong>{" "}
                Each guide leads with what these learners bring, not what
                they lack.
              </span>
            </li>
            <li className="flex items-start">
              <span className="ae-marker mt-2.5" />
              <span>
                <strong className="text-[#1c2d3d]">Teacher voice.</strong>{" "}
                A trusted colleague who has been in the room sharing what
                works — not an expert lecturing.
              </span>
            </li>
            <li className="flex items-start">
              <span className="ae-marker mt-2.5" />
              <span>
                <strong className="text-[#1c2d3d]">No student data.</strong>{" "}
                The generators we link to take a typed description of a
                need. No names, no identifiers — and nothing is stored.
              </span>
            </li>
          </ul>
        </section>
      </main>
        </div>
      )}
    </AuthAwareSupportLayout>
  );
}
